import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Paperclip, X, FileText, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';

const SicknessForm = ({ onSubmit, onCancel }) => {
  const [protocolNumber, setProtocolNumber] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [doctor, setDoctor] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`File troppo grande: ${f.name}`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!protocolNumber.trim()) {
      toast.error('Inserisci il numero di protocollo INPS');
      return;
    }
    if (!/^\d{10,}$/.test(protocolNumber.replace(/\s/g, ''))) {
      toast.error('Il protocollo INPS deve essere un numero di almeno 10 cifre');
      return;
    }
    if (!startDate) {
      toast.error('Inserisci la data di inizio malattia');
      return;
    }
    if (endDate && new Date(endDate) < new Date(startDate)) {
      toast.error('La data di fine prevista non può essere precedente alla data di inizio');
      return;
    }
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));
    onSubmit({
      protocolNumber: protocolNumber.replace(/\s/g, ''),
      startDate,
      endDate,
      doctor,
      notes,
      attachments: files.map(f => ({ name: f.name, size: f.size }))
    });
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="protocol" className="text-sm font-medium">Numero protocollo INPS <span className="text-red-500">*</span></Label>
        <Input
          id="protocol"
          placeholder="es. 12345678901"
          value={protocolNumber}
          onChange={(e) => setProtocolNumber(e.target.value)}
          className="h-11 font-mono"
          required
        />
        <p className="text-[11px] text-slate-500">Il numero di protocollo rilasciato dal medico al momento del certificato</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="sstart" className="text-sm font-medium">Data inizio malattia <span className="text-red-500">*</span></Label>
          <div className="relative">
            <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input id="sstart" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-11 pl-9" required />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="send" className="text-sm font-medium">Data fine prevista</Label>
          <div className="relative">
            <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input id="send" type="date" min={startDate} value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-11 pl-9" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="doctor" className="text-sm font-medium">Medico curante</Label>
        <Input id="doctor" placeholder="Dr. Nome Cognome" value={doctor} onChange={(e) => setDoctor(e.target.value)} className="h-11" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="snotes" className="text-sm font-medium">Note</Label>
        <Textarea id="snotes" placeholder="Eventuali informazioni aggiuntive..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="resize-none" />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Certificato medico (opzionale)</Label>
        <label className="flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer transition-colors">
          <Paperclip className="w-4 h-4" />
          <span>Carica certificato (PDF, immagini)</span>
          <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileChange} className="hidden" />
        </label>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {files.map((f, i) => (
              <div key={`${f.name}-${f.size}-${i}`} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs">
                <FileText className="w-3.5 h-3.5 text-slate-600" />
                <span className="font-medium truncate max-w-[180px]">{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>}
        <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800">
          {submitting ? 'Invio in corso...' : 'Invia protocollo'}
        </Button>
      </div>
    </form>
  );
};

export default SicknessForm;
